import { useCallback, useEffect, useRef, useState } from 'react'
import { encodeMorse, morseToDisplay, wpmToDotDuration } from '../core/morse'
import { isPassingSession, type SessionResult } from '../core/progress'
import type { LessonDefinition, LessonId } from '../data/lessons'
import { useMorseInput, type MorseInput } from '../hooks/useMorseInput'
import type { AppSettings } from '../types/settings'
import { keyCodeLabel } from '../types/settings'
import { ArrowIcon, BackIcon, CheckIcon, KeyboardIcon, RabbitMark, SettingsIcon, SoundIcon } from './Icons'

type Phase = 'ready' | 'active' | 'complete'

interface Answer {
  target: string
  entered: string
  sequence: string
  correct: boolean
}

interface Feedback {
  correct: boolean
  entered: string
}

interface PracticeViewProps {
  lesson: LessonDefinition
  settings: AppSettings
  inputEnabled: boolean
  onOpenSettings: () => void
  onExit: () => void
  onComplete: (result: SessionResult) => void
  onOpenLesson: (lessonId: LessonId) => void
  nextLessonId?: LessonId
}

function createQuestions(lesson: LessonDefinition): string[] {
  const fresh = [...lesson.newCharacters]
  const pool = [...lesson.characters]
  const questions: string[] = []

  while (questions.length < lesson.passCriteria.minAttempts) {
    const source = questions.length < Math.min(fresh.length * 2, 6) ? fresh : pool
    const options = source.filter((character) => character !== questions.at(-1))
    questions.push(options[Math.floor(Math.random() * options.length)] ?? source[0])
  }

  return questions
}

function useMorsePreview(settings: AppSettings) {
  const contextRef = useRef<AudioContext | null>(null)

  useEffect(() => () => {
    void contextRef.current?.close()
  }, [])

  return useCallback((character: string) => {
    const sequence = encodeMorse(character)
    if (!sequence || settings.volume <= 0) return

    const AudioContextClass = window.AudioContext
    const context = contextRef.current ?? new AudioContextClass()
    contextRef.current = context
    if (context.state === 'suspended') void context.resume()

    const oscillator = context.createOscillator()
    const gain = context.createGain()
    const unit = wpmToDotDuration(settings.wpm) / 1000
    let cursor = context.currentTime + 0.04

    oscillator.type = 'sine'
    oscillator.frequency.value = settings.frequency
    gain.gain.setValueAtTime(0, context.currentTime)
    oscillator.connect(gain).connect(context.destination)

    for (const symbol of sequence) {
      const duration = symbol === '.' ? unit : unit * 3
      gain.gain.setValueAtTime(settings.volume, cursor)
      gain.gain.setValueAtTime(settings.volume, cursor + duration)
      gain.gain.setValueAtTime(0, cursor + duration + 0.005)
      cursor += duration + unit
    }

    oscillator.start()
    oscillator.stop(cursor + 0.03)
  }, [settings.frequency, settings.volume, settings.wpm])
}

interface KeySurfaceProps {
  input: MorseInput
  label: string
  symbol: string
  keyLabel: string
  active: boolean
  press: (input: MorseInput) => void
  release: (input: MorseInput) => void
  className?: string
}

function KeySurface({ input, label, symbol, keyLabel, active, press, release, className = '' }: KeySurfaceProps) {
  const finish = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    release(input)
  }

  return (
    <button
      className={`key-surface ${className} ${active ? 'is-active' : ''}`}
      type="button"
      aria-pressed={active}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        press(input)
      }}
      onPointerUp={finish}
      onPointerCancel={finish}
      onLostPointerCapture={finish}
    >
      <span className="key-symbol">{symbol}</span>
      <span className="key-name">{label}</span>
      <kbd>{keyLabel}</kbd>
    </button>
  )
}

export function PracticeView({
  lesson,
  settings,
  inputEnabled,
  onOpenSettings,
  onExit,
  onComplete,
  onOpenLesson,
  nextLessonId,
}: PracticeViewProps) {
  const [phase, setPhase] = useState<Phase>('ready')
  const [questions, setQuestions] = useState<string[]>(() => createQuestions(lesson))
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [startedAt, setStartedAt] = useState(0)
  const [bestStreak, setBestStreak] = useState(0)
  const streakRef = useRef(0)
  const recordedRef = useRef(false)
  const timeoutRef = useRef<number | null>(null)
  const playPreview = useMorsePreview(settings)

  const target = questions[questionIndex] ?? lesson.characters[0]
  const sessionLength = lesson.passCriteria.minAttempts
  const activeRef = useRef({ phase, feedback, target, questionIndex })
  activeRef.current = { phase, feedback, target, questionIndex }

  const handleCharacter = useCallback((entered: string, sequence: string) => {
    const state = activeRef.current
    if (state.phase !== 'active' || state.feedback) return

    const correct = entered.toUpperCase() === state.target.toUpperCase()
    const answer: Answer = { target: state.target, entered, sequence, correct }
    setAnswers((current) => [...current, answer])
    setFeedback({ correct, entered })

    streakRef.current = correct ? streakRef.current + 1 : 0
    setBestStreak((current) => Math.max(current, streakRef.current))

    timeoutRef.current = window.setTimeout(() => {
      if (state.questionIndex + 1 >= sessionLength) {
        setPhase('complete')
      } else {
        setQuestionIndex((current) => current + 1)
        setFeedback(null)
      }
    }, correct ? 540 : 850)
  }, [sessionLength])

  const input = useMorseInput({
    mode: settings.mode,
    wpm: settings.wpm,
    frequency: settings.frequency,
    volume: settings.volume,
    keyBindings: settings.keyBindings,
    enabled: inputEnabled && phase === 'active' && feedback === null,
    onCharacter: handleCharacter,
  })

  useEffect(() => () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
  }, [])

  useEffect(() => {
    if (phase !== 'complete' || answers.length !== sessionLength || recordedRef.current) return
    recordedRef.current = true
    const correct = answers.filter((answer) => answer.correct).length
    onComplete({
      lessonId: lesson.id,
      attempts: answers.length,
      correct,
      durationMs: Math.max(1, Date.now() - startedAt),
      bestStreak,
      completedAt: new Date().toISOString(),
    })
  }, [answers, bestStreak, lesson.id, onComplete, phase, sessionLength, startedAt])

  const correctCount = answers.filter((answer) => answer.correct).length
  const finalResult: SessionResult = {
    lessonId: lesson.id,
    attempts: answers.length,
    correct: correctCount,
    durationMs: Math.max(1, Date.now() - startedAt),
    bestStreak,
  }
  const passed = answers.length > 0 && isPassingSession(finalResult)
  const progress = phase === 'complete' ? 100 : (questionIndex / sessionLength) * 100
  const inputDisplay = input.sequence ? morseToDisplay(input.sequence) : '入力待ち'

  const start = () => {
    const nextQuestions = createQuestions(lesson)
    setQuestions(nextQuestions)
    setQuestionIndex(0)
    setAnswers([])
    setFeedback(null)
    setBestStreak(0)
    streakRef.current = 0
    recordedRef.current = false
    setStartedAt(Date.now())
    input.reset()
    setPhase('active')
  }

  if (phase === 'ready') {
    return (
      <main className="practice-shell practice-ready page-enter">
        <div className="practice-topbar">
          <button className="back-button" type="button" onClick={onExit}><BackIcon /> コースへ</button>
          <button className="icon-button" type="button" onClick={onOpenSettings} aria-label="入力設定"><SettingsIcon /></button>
        </div>
        <section className="ready-card">
          <div className="ready-art" aria-hidden="true">
            <RabbitMark size={104} />
            <span className="ready-code">· — &nbsp; · ·</span>
          </div>
          <span className="level-pill">LEVEL {lesson.level}</span>
          <h1>{lesson.title}</h1>
          <p>{lesson.description}</p>
          <div className="ready-characters">
            {lesson.newCharacters.map((character) => (
              <div key={character}><strong>{character}</strong><span>{morseToDisplay(encodeMorse(character) ?? '')}</span></div>
            ))}
          </div>
          <div className="ready-meta">
            <span><i>{sessionLength}</i> 問</span>
            <span><i>{Math.round(lesson.passCriteria.minAccuracy * 100)}%</i> で合格</span>
            <span><i>{settings.wpm}</i> WPM</span>
          </div>
          <button className="primary-button ready-start" type="button" onClick={start}>練習をはじめる <ArrowIcon /></button>
          <small className="ready-tip"><KeyboardIcon size={17} /> 音を出すには、ブラウザの音量をオンにしてください</small>
        </section>
      </main>
    )
  }

  if (phase === 'complete') {
    const accuracy = Math.round((correctCount / Math.max(answers.length, 1)) * 100)
    return (
      <main className="practice-shell result-view page-enter">
        <section className={`result-card ${passed ? 'passed' : ''}`}>
          <div className="result-rabbit"><RabbitMark size={88} /><span>{passed ? 'NICE!' : 'ONE MORE!'}</span></div>
          <span className="eyebrow">SESSION COMPLETE</span>
          <h1>{passed ? 'レベルクリア！' : 'あともう少し！'}</h1>
          <p>{passed ? '指がリズムを覚えてきました。次へ進もう。' : `合格ラインは ${Math.round(lesson.passCriteria.minAccuracy * 100)}%。もう一度やればきっと届きます。`}</p>
          <div className="result-score">
            <div className="score-ring" style={{ '--score': `${accuracy * 3.6}deg` } as React.CSSProperties}><strong>{accuracy}<small>%</small></strong></div>
            <div className="score-details">
              <span><small>正解</small><strong>{correctCount} <i>/ {answers.length}</i></strong></span>
              <span><small>ベスト連続</small><strong>{bestStreak} <i>問</i></strong></span>
            </div>
          </div>
          <div className="answer-strip" aria-label="回答結果">
            {answers.map((answer, index) => <span className={answer.correct ? 'correct' : 'wrong'} key={`${answer.target}-${index}`} title={`${answer.target}: ${answer.entered || '?'}`}>{answer.target}</span>)}
          </div>
          <div className="result-actions">
            {passed && nextLessonId ? (
              <button className="primary-button" type="button" onClick={() => onOpenLesson(nextLessonId)}>次のレベルへ <ArrowIcon /></button>
            ) : (
              <button className="primary-button" type="button" onClick={start}>もう一度練習する <ArrowIcon /></button>
            )}
            <button className="text-button" type="button" onClick={onExit}>コースに戻る</button>
          </div>
        </section>
      </main>
    )
  }

  return (
    <main className="practice-shell active-practice page-enter">
      <div className="practice-topbar">
        <button className="back-button" type="button" onClick={onExit}><BackIcon /> 終了</button>
        <div className="practice-level"><span>LEVEL {lesson.level}</span><strong>{lesson.title}</strong></div>
        <button className="icon-button" type="button" onClick={onOpenSettings} aria-label="入力設定"><SettingsIcon /></button>
      </div>

      <div className="question-progress" aria-label={`${questionIndex + 1}問目 / ${sessionLength}問`}>
        <span style={{ width: `${progress}%` }} />
      </div>

      <section className={`prompt-card ${feedback ? (feedback.correct ? 'feedback-correct' : 'feedback-wrong') : ''}`}>
        <div className="prompt-meta"><span>QUESTION {String(questionIndex + 1).padStart(2, '0')}</span><span>{correctCount} correct</span></div>
        {feedback ? (
          <div className="feedback-content" aria-live="assertive">
            <span className="feedback-icon">{feedback.correct ? <CheckIcon size={36} /> : '×'}</span>
            <strong>{feedback.correct ? 'その調子！' : 'おしい！'}</strong>
            {!feedback.correct && <small>{feedback.entered ? `${feedback.entered} と読み取りました` : '符号を確認してみよう'}</small>}
          </div>
        ) : (
          <>
            <p>この文字をキーイング</p>
            <h1>{target}</h1>
            <button className="listen-button" type="button" onClick={() => playPreview(target)}><SoundIcon /> お手本を聞く</button>
            <div className={`morse-hint ${settings.showHint ? '' : 'is-hidden'}`}>
              <small>MORSE</small><strong>{settings.showHint ? morseToDisplay(encodeMorse(target) ?? '') : '••••'}</strong>
            </div>
          </>
        )}
      </section>

      <section className="input-zone" aria-label="モールス入力">
        <div className="live-sequence" aria-live="polite">
          <span className={input.sequence ? 'has-input' : ''}>{inputDisplay}</span>
          <i className={input.sequence ? 'typing' : ''} />
        </div>

        {settings.mode === 'paddle' ? (
          <div className="paddle-controls">
            <KeySurface input="dit" label="短点" symbol="·" keyLabel={keyCodeLabel(settings.keyBindings.dit)} active={input.isDitPressed} press={input.press} release={input.release} className="dit-key" />
            <KeySurface input="dah" label="長点" symbol="—" keyLabel={keyCodeLabel(settings.keyBindings.dah)} active={input.isDahPressed} press={input.press} release={input.release} className="dah-key" />
          </div>
        ) : (
          <div className="straight-control">
            <KeySurface input="straight" label="押してキーイング" symbol="" keyLabel={keyCodeLabel(settings.keyBindings.straight)} active={input.isStraightPressed} press={input.press} release={input.release} className="straight-key" />
          </div>
        )}

        <div className="input-help">
          <KeyboardIcon size={18} />
          {settings.mode === 'paddle'
            ? <span><kbd>{keyCodeLabel(settings.keyBindings.dit)}</kbd> 短点 <b>＋</b> <kbd>{keyCodeLabel(settings.keyBindings.dah)}</kbd> 長点</span>
            : <span><kbd>{keyCodeLabel(settings.keyBindings.straight)}</kbd> 短く押す <b>·</b> 長く押す <b>—</b></span>}
          <small>画面のキーもタッチできます</small>
        </div>
      </section>
    </main>
  )
}
